import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'

import {
  DiscountCircleText,
  DiscountIcon,
  IconTextAndDetailsContainer,
  RewardNameContainer,
  TextAndIconContainer,
} from './CheckoutRewards.styles'

import { NarrowFulfilmentMethodInputType } from '@src/graphql-types'

import { ButtonType } from '@src/components/Button'
import {
  DiscountForm,
  ApplyButton,
  VoucherWarningContainer,
  VoucherWarning,
  ActiveDiscount,
  ActiveDiscountContainer,
  ActiveDiscountName,
  ClearButton,
  SpaceBetweenButton,
  SpaceBetweenRow,
  Title,
} from '@src/components/CheckoutModal/BasketSummary/DiscountCodeSection/DiscountCodeSection.styles'
import { TextInput } from '@src/components/Inputs'
import { useDiscountCode } from '@src/hooks/useDiscountCodes/useDiscountCode'
import { useDiscounts } from '@src/hooks/useDiscounts/useDiscounts'
import { DiscountType } from '@src/hooks/useDiscounts/validation'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { Formik } from 'formik'
import { toUpper } from 'lodash'
import { DotSpan } from '@src/components/DotSpan/DotSpan'
import { CurrentFulfilmentType } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/validation'

export const DiscountCodeDealsSection: React.FC = () => {
  const { t } = useTranslation(['customerLoyaltyCard'])
  const { basketTotals } = useBasketTotalsContext()

  const outletFulfilment = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  const marketplace = useMarketplace()

  const checkout = useCheckoutRouter()

  const { discounts, updateDiscount } = useDiscounts()

  const {
    discountError,
    discountCodeOpen,
    isVoucherExceedingBasketSubtotal,
    setIsVoucherExceedingBasketSubtotal,
    loading,
    setDiscountCodeOpen,
    handleSubmit,
  } = useDiscountCode({
    basketSubTotal:
      basketTotals?.data?.checkoutBasketTotals?.nonAddOnBasketItemsTotal || 0,
    outletId: outletFulfilment.outlet.id,
    marketplaceId: marketplace.id,
    onDiscountApplied: id =>
      updateDiscount({
        type: DiscountType.VOUCHER_DISCOUNT,
        data: id,
      }),
  })

  const isBasketView =
    checkout.route === CheckoutRoute.BASKET || checkout.route === undefined

  const appliedDiscount =
    basketTotals?.data?.checkoutBasketTotals?.appliedDiscount?.discount || null

  const hasDiscountCodeApplied = !!appliedDiscount?.voucher

  if (
    (!isBasketView && !hasDiscountCodeApplied) ||
    [
      CurrentFulfilmentType.TABLE,
      CurrentFulfilmentType.TABLE_UNSELECTED,
    ].includes(outletFulfilment.data.currentFulfilment.type)
  ) {
    return null
  }

  return (
    <>
      <IconTextAndDetailsContainer data-test-id="checkout-discount-section">
        <TextAndIconContainer>
          <DiscountIcon>
            <DiscountCircleText
              largeText
              data-test-id="checkout-discount-badge-value"
            >
              %
            </DiscountCircleText>
          </DiscountIcon>

          <RewardNameContainer
            color={'discount'}
            data-test-id="checkout-discount-title"
          >
            {t('discount_code')}
          </RewardNameContainer>
        </TextAndIconContainer>

        {outletFulfilment.data.currentFulfilment.narrowType !==
          NarrowFulfilmentMethodInputType.TABLE &&
        basketTotals?.data?.checkoutBasketTotals?.nonAddOnBasketItemsTotal ? (
          <>
            {appliedDiscount &&
            appliedDiscount.id === discounts.voucherDiscountId ? (
              <SpaceBetweenRow data-test-id="checkout-discount-applied-row">
                <ActiveDiscountContainer>
                  <ActiveDiscount data-test-id="checkout-discount-applied-label">
                    {t('discount_applied', { ns: 'discounts' })}
                  </ActiveDiscount>

                  <DotSpan />

                  <ActiveDiscountName data-test-id="checkout-discount-name">
                    {appliedDiscount.name}
                  </ActiveDiscountName>
                </ActiveDiscountContainer>

                <ClearButton
                  data-test-id="checkout-discount-clear-button"
                  onClick={() => {
                    updateDiscount({
                      type: DiscountType.VOUCHER_DISCOUNT,
                      data: null,
                    })
                    setIsVoucherExceedingBasketSubtotal(false)
                  }}
                  id={t('clear', { ns: 'discounts' })}
                />
              </SpaceBetweenRow>
            ) : (
              <SpaceBetweenButton
                discountOpen={discountCodeOpen}
                onClick={() => setDiscountCodeOpen(!discountCodeOpen)}
              >
                <Title data-test-id="checkout-discount-toggle-button">
                  {t('have_a_discount', { ns: 'discounts' })}
                </Title>
              </SpaceBetweenButton>
            )}
          </>
        ) : null}
      </IconTextAndDetailsContainer>

      {discountCodeOpen && (
        <IconTextAndDetailsContainer data-test-id="checkout-discount-form-container">
          <Formik initialValues={{ discount: '' }} onSubmit={handleSubmit}>
            <DiscountForm data-test-id="checkout-discount-form">
              <TextInput
                name="discount"
                label={t('enter_discount_code', { ns: 'discounts' })}
                disabled={loading}
                autoComplete="off"
                fullWidth
                toUpperCase
                error={discountError}
                dataTestId="checkout-discount-input"
              />

              <ApplyButton
                data-test-id="checkout-discount-apply-button"
                content={toUpper(t('apply'))}
                type="submit"
                disabled={loading}
                buttonType={ButtonType.SECONDARY}
              />
            </DiscountForm>
          </Formik>

          {isVoucherExceedingBasketSubtotal && (
            <VoucherWarningContainer data-test-id="checkout-discount-warning-container">
              <VoucherWarning data-test-id="checkout-discount-warning-text">
                {t('discount_exceeds_total', { ns: 'discounts' })}
              </VoucherWarning>
            </VoucherWarningContainer>
          )}
        </IconTextAndDetailsContainer>
      )}
    </>
  )
}
